import React, { useState, useEffect } from "react";
import axios from "./axiosInstance";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faSpinner } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { getServicePath } from "./data/serviceOptions";

const getRecords = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};
const setRecords = (key, val) => localStorage.setItem(key, JSON.stringify(val));
const toBackendPayload = (record) => {
  const {
    name,
    dob,
    gender,
    age,
    height,
    weight,
    heightUnit,
    weightUnit,
    bmi,
    bmiCategory,
    waistCircumferenceCm,
    waistCategory,
    bloodPressure,
    pulse,
    blood,
    glucose,
    additionalResults,
    cholesterol,
    weeklyUnitIntake,
    weeklyHours,
    smokerStatus,
    services,
    routineCancerChecks,
    recommendedServices,
    additionalNotes,
    familyMedicalHistory,
    isActiveTreatment,
    activeTreatmentNotes,
    email,
    optOut,
    assessingNurse,
    selectedSchool,
  } = record;
  return {
    name,
    dob,
    gender,
    age,
    height,
    weight,
    heightUnit,
    weightUnit,
    bmi,
    bmiCategory,
    waistCircumferenceCm,
    waistCategory,
    bloodPressure,
    pulse,
    blood,
    glucose,
    additionalResults,
    cholesterol,
    weeklyUnitIntake,
    weeklyHours,
    smokerStatus,
    services,
    routineCancerChecks,
    recommendedServices,
    additionalNotes,
    familyMedicalHistory,
    isActiveTreatment,
    activeTreatmentNotes,
    email,
    optOut,
    assessingNurse,
    selectedSchool,
  };
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const m = moment(iso);
  return moment().diff(m, "hours") < 24
    ? m.fromNow()
    : m.format("DD/MM/YYYY HH:mm");
};

const Dashboard = () => {
  const [unsentForms, setUnsentForms] = useState([]);
  const [submittingId, setSubmittingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUnsentForms = () => {
      const savedForms =
        JSON.parse(localStorage.getItem("healthScreenData")) || [];
      const sorted = [...savedForms].sort(
        (newerForm, olderForm) =>
          moment(olderForm.createdAt).valueOf() -
          moment(newerForm.createdAt).valueOf(),
      );
      setUnsentForms(sorted);
    };

    fetchUnsentForms();

    window.addEventListener("storage", fetchUnsentForms);

    return () => {
      window.removeEventListener("storage", fetchUnsentForms);
    };
  }, []);

  const refreshFromStorage = () => {
    setUnsentForms(getRecords("healthScreenData"));
  };

  const updateArchiveStatus = (record, status, extra = {}) => {
    let archive = getRecords("allHealthScreenData");
    const updated = {
      ...record,
      status,
      updatedAt: moment().toISOString(),
      ...extra,
    };
    const idx = archive.findIndex((r) => r.id === record.id);
    if (idx > -1) {
      archive[idx] = { ...archive[idx], ...updated };
    } else {
      archive.push(updated);
    }
    setRecords("allHealthScreenData", archive);
  };

  const handleSubmitOne = async (rec) => {
    if (!navigator.onLine) {
      Swal.fire(
        "Offline",
        "No internet connection. Try again later.",
        "warning",
      );
      return;
    }
    setSubmittingId(rec.id);
    try {
      const payload = toBackendPayload(rec);
      const response = await axios.post("health-screen-report/create", payload);

      if (response.status === 200) {
        // remove from healthScreenData
        const queue = getRecords("healthScreenData").filter(
          (r) => r.id !== rec.id,
        );
        setRecords("healthScreenData", queue);

        updateArchiveStatus(rec, "submitted", {
          submittedAt: moment().toISOString(),
          backendId: response?.data?.id ?? rec.backendId ?? null,
          errorMessage: null,
        });

        Swal.fire("Submitted", `${rec.name}'s form was submitted.`, "success");
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (err) {
      console.error("Submit failed:", err);
      let combinedMessage = err?.message || "Unknown error";
      if (err.response?.data?.message) {
        combinedMessage += ` ${err.response.data.message}`;
      }
      let queue = getRecords("healthScreenData");
      queue = queue.map((record) =>
        record.id === rec.id
          ? {
              ...record,
              status: "error",
              errorMessage: combinedMessage,
              updatedAt: moment().toISOString(),
            }
          : record,
      );
      setRecords("healthScreenData", queue);

      //archived
      let archive = getRecords("allHealthScreenData");
      archive = archive.map((record) =>
        record.id === rec.id
          ? {
              ...record,
              status: "error",
              errorMessage: combinedMessage,
              updatedAt: moment().toISOString(),
            }
          : record,
      );
      setRecords("allHealthScreenData", archive);

      Swal.fire("Error", "There was an error submitting this form.", "error");
    } finally {
      setSubmittingId(null);
      refreshFromStorage();
    }
  };

  // const handleSaveAll = async () => {
  //   if (!navigator.onLine) {
  //     alert("No internet connection. Try again later.")
  //     return
  //   }

  //   if (unsentForms.length === 0) {
  //     alert("No unsent forms to save.")
  //     return
  //   }

  //   const savePromises = unsentForms.map(async (form, index) => {
  //     try {
  //       const response = await axios.post("health-screen-report/create", form)

  //       if (response.status === 200) {
  //         console.log(`Form ${index + 1} saved successfully.`)
  // onClick={() =>
  //   navigate(`/screen/${getServicePath(form.services)}`, {
  //         throw new Error(`Form ${index + 1} failed`)
  //       }
  //     } catch (error) {
  //       console.error(`Error sending form ${index + 1}:`, error)
  //       return { status: "rejected", error }
  //     }
  //   })

  //   const results = await Promise.allSettled(savePromises)
  //   const failedForms = unsentForms.filter((_, index) => results[index].status === "rejected")

  //   if (failedForms.length === 0) {
  //     Swal.fire({
  //       title: 'All forms Sent!',
  //       text: 'The health screen results have been submitted.',
  //       icon: 'success',
  //     })
  //     localStorage.removeItem("healthScreenData")
  //     setUnsentForms([]);
  //   } else {
  //     Swal.fire({
  //       title: 'Error Occured!',
  //       text: 'There was an error submitting your results, please try again',
  //       icon: 'error',
  //     })
  //     localStorage.setItem("healthScreenData", JSON.stringify(failedForms))
  //     setUnsentForms(failedForms)
  //   }
  // }

  return (
    <div className="relative container mx-auto p-4">
      <button
        className="absolute top-0 right-0 rounded-lg px-4 py-2 bg-sas-primary text-white hover:bg-blue-700 mt-4 mr-4"
        onClick={() => navigate("/")}
      >
        Back to Health Screening
      </button>
      <div className="mx-auto max-w-4xl mt-8">
        <h2 className="text-2xl font-bold text-sas-primary mb-4">
          Unsent Health Screenings
        </h2>

        {unsentForms.length === 0 ? (
          <p className="text-gray-600">No unsent forms available.</p>
        ) : (
          <>
            <ul className="space-y-2">
              {unsentForms.map((form) => (
                <li
                  key={form.id ?? form.createdAt}
                  className="bg-white border rounded-md shadow p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <div className="font-semibold text-sas-primary text-lg">
                        {form.name}{" "}
                        {form.age ? (
                          <span className="text-gray-600 font-normal">
                            {" "}
                            · {form.age}
                          </span>
                        ) : null}
                        {form.gender ? (
                          <span className="text-gray-600 font-normal">
                            {" "}
                            · {form.gender}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-sm text-gray-700">
                        <span className="mr-4">
                          <strong>Email:</strong> {form.email || "—"}
                        </span>
                        <span className="mr-4 capitalize">
                          <strong>Status:</strong>{" "}
                          <span
                            className={`ml-1 inline-block px-2 py-0.5 rounded text-xs
                            ${form.status === "error" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}
                          >
                            {form.status ?? "pending"}
                          </span>
                        </span>
                        <span>
                          <strong>Created:</strong>{" "}
                          {formatDateTime(form.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        className="inline-flex items-center gap-2 border border-sas-primary text-sas-primary bg-white px-3 py-2 rounded-md font-semibold hover:bg-sas-primary hover:text-white transition disabled:opacity-60"
                        onClick={() =>
                          navigate(`/screen/${getServicePath(form.services)}`, {
                            state: {
                              source: "dashboard",
                              record: form,
                              returnTo: "/dashboard",
                            },
                          })
                        }
                        disabled={Boolean(submittingId)}
                        title="Edit this form before submitting"
                      >
                        Edit
                      </button>
                      <button
                        className="inline-flex items-center gap-2 bg-sas-primary text-white px-3 py-2 rounded-md font-semibold hover:bg-sas-primary-hover transition disabled:opacity-60"
                        onClick={() => {
                          if (!submittingId) handleSubmitOne(form);
                        }}
                        disabled={Boolean(submittingId)}
                        aria-busy={form.id === submittingId}
                        title={
                          submittingId
                            ? "Please wait for the current submission to complete"
                            : "Submit this form"
                        }
                      >
                        {form.id === submittingId ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            Submit
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {form.errorMessage && form.status === "error" && (
                    <div className="mt-2 text-sm text-red-700">
                      <strong>Error:</strong> {form.errorMessage}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* <button
              onClick={handleSaveAll}
              className="mt-4 bg-sas-primary text-white py-2 px-4 rounded-md font-semibold hover:bg-sas-primary-hover transition"
            >
              Save All
            </button> */}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
